import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SimilarCard.module.css';
import { FlatCardData, ParkingCardData, BusinessCardData } from '../CardsComponents/types';
import CardIconButton from '../UI/CardIconButton/CardIconButton';
import { useFavorite } from '../../pages/FavoritePage/FavoriteContext';
import { useCompare } from '../../pages/ComparePage/CompareContext';
import { calculateDiscountedPrice, calculateDiscountedPricePerM2 } from '../../utils/discountPriceUtils';
import cn from 'classnames';

type PropertyData = FlatCardData | ParkingCardData | BusinessCardData;

interface CardProps {
  data: PropertyData;
  isDiscounted: boolean;
  selectedOption?: 'full' | 'installment';
}

export default function SimilarCard({ data, selectedOption = 'full' }: CardProps) {
  const navigate = useNavigate();
  const { favorites, toggleFavorite } = useFavorite();
  const { compares, toggleCompare } = useCompare();
  const [imageIndex, setImageIndex] = useState(0);
  const [isImageVisible, setIsImageVisible] = useState(true);

  const isFlat = (data: PropertyData): data is FlatCardData => (data as FlatCardData).roomType !== undefined;
  const isParking = (data: PropertyData): data is ParkingCardData => (data as ParkingCardData).numberPlace !== undefined;
  const isBusiness = (data: PropertyData): data is BusinessCardData => (data as BusinessCardData).corpus !== undefined;

  const isFavorite = favorites.some((item) => item.id === data.id && item.type === data.type);
  const isCompared = compares.some((item) => item.id === data.id && item.type === data.type);

  const handleToggleFavorite = () => {
    const adaptedData: FlatCardData = {
      ...data,
      type: data.type,
      roomType: '',
      entrance: 0,
      projectType: '',
      cellingHeight: 0,
      checkIn: '',
      image: '',
      imageSecond: '',
      windowOptions: [],
      specialOptions: [],
      totalEntrances: 0,
      livingArea: '',
    };

    toggleFavorite(adaptedData);
  };

  const handleToggleCompare = () => {
    const adaptedData: FlatCardData = {
      ...data,
      type: data.type,
      roomType: '',
      entrance: 0,
      projectType: '',
      cellingHeight: 0,
      checkIn: '',
      image: '',
      imageSecond: '',
      windowOptions: [],
      specialOptions: [],
      totalEntrances: 0,
      livingArea: '',
    };

    toggleCompare(adaptedData);
  };

  const handleCardClick = () => {
    navigate(`/${data.type}/status/${data.id}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const normalizeImagePath = (value: string | undefined | null): string => {
    if (!value) return '';

    const normalized = value.trim();
    if (!normalized) return '';

    if (normalized.startsWith('//')) {
      return `https:${normalized}`;
    }
    if (/^http:\/\//i.test(normalized)) {
      return `https://${normalized.slice('http://'.length)}`;
    }
    if (/^(https:\/\/|data:|blob:)/i.test(normalized)) {
      return normalized;
    }

    return normalized.startsWith('/') ? normalized : `/${normalized}`;
  };

  const imageCandidates = useMemo(() => {
    const candidates: Array<string | undefined> = [];

    if (isFlat(data)) {
      candidates.push(data.image, data.imageSecond, data.previewPlan, data.previewFloor, data.previewVisual);
    }
    if (isBusiness(data)) {
      candidates.push(data.image, data.imageSecond, data.previewPlan, data.previewFloor, data.previewVisual);
    }

    const normalized = candidates.map(candidate => normalizeImagePath(candidate)).filter(Boolean);
    return Array.from(new Set(normalized));
  }, [data]);

  const imageCandidatesKey = imageCandidates.join('|');
  const currentImage = imageCandidates[imageIndex];

  useEffect(() => {
    setImageIndex(0);
    setIsImageVisible(true);
  }, [data.id, data.type, imageCandidatesKey]);

  const handleImageError = () => {
    if (imageIndex < imageCandidates.length - 1) {
      setImageIndex(prev => prev + 1);
      return;
    }

    setIsImageVisible(false);
  };

  const formatArea = (value: string | number) => {
    const numericValue = Number(String(value).replace(',', '.').replace(/\s/g, ''));
    if (Number.isNaN(numericValue)) {
      return String(value || '').trim();
    }
    return numericValue.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  };

  const renderDetails = () => {
    const discountedPrice = calculateDiscountedPrice(data.pricePerM2 ?? '0', data.discountProperty ?? 0, data.price ?? '0');
    const discountedPricePerM2 = data.pricePerM2 && data.discountProperty
      ? calculateDiscountedPricePerM2(data.pricePerM2, data.discountProperty)
      : data.pricePerM2;

    if (isFlat(data)) {
      return (
        <>
          <h2 className={cn('text_subheaders_sh1', styles.property_title)}>{`${data.roomType} от ${formatArea(data.area)} м²`}</h2>
          <div className={styles.details_row}>
            <div className={styles.price_section}>
              <div className={styles.price_label_group}>
                <span className={cn('text_body_b3', styles.label)}>Стоимость</span>
                <span className={cn('text_body_b3', styles.label)}>Цена за м²</span>
              </div>
              <div className={styles.price_label_group}>
                <span className={cn('text_body_b3', styles.value)}>от <span>{discountedPrice}</span> ₽</span>
                <span className={cn('text_body_b3', styles.value)}>
                  от {selectedOption === 'full' ? discountedPricePerM2 : data.pricePerM2} ₽
                </span>
              </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.location_details}>
              <span className={cn('text_body_b3', styles.detail_text)}>{`${data.entrance} из ${data.totalEntrances} подъезд, ${data.floor} из ${data.totalFloors} этаж`}</span>
              <span className={cn('text_body_b3', styles.detail_text)}>Заселение {data.checkIn}</span>
            </div>
          </div>
        </>
      );
    }
    if (isParking(data)) {
      return (
        <>
          <h2 className={cn('text_subheaders_sh1', styles.property_title)}>{`Машино-место ${data.area} м²`}</h2>
          <div className={styles.details_row}>
            <div className={styles.price_section}>
              <div className={styles.price_label_group}>
                <span className={cn('text_body_b3', styles.label)}>Стоимость</span>
              </div>
              <div className={styles.price_label_group}>
                <span className={cn('text_body_b3', styles.value)}>{discountedPrice} ₽</span>
              </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.location_details}>
              <span className={cn('text_body_b3', styles.detail_text)}>
                {`${data.numberPlace} из ${data.totalNumberPlaces} номер, ${data.floor} из ${data.totalFloors} этаж`}
              </span>
              <span className={cn('text_body_b3', styles.detail_text)}>Передача {data.checkIn}</span>
            </div>
          </div>
        </>
      );
    }
    if (isBusiness(data)) {
      return (
        <>
          <h2 className={cn('text_subheaders_sh1', styles.property_title)}>{`Помещение ${data.area} м²`}</h2>
          <div className={styles.details_row}>
            <div className={styles.price_section}>
              <div className={styles.price_label_group}>
                <span className={cn('text_body_b3', styles.label)}>Стоимость</span>
                <span className={cn('text_body_b3', styles.label)}>Цена за м²</span>
              </div>
              <div className={styles.price_label_group}>
                <span className={cn('text_body_b3', styles.value)}>от <span>{discountedPrice}</span> ₽</span>
                <span className={cn('text_body_b3', styles.value)}>от {data.pricePerM2} ₽</span>
              </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.location_details}>
              <span className={cn('text_body_b3', styles.detail_text)}>{`${data.numberPlace} из ${data.totalNumberPlaces} номер, ${data.floor} из ${data.totalFloors} этаж`}</span>
              <span className={cn('text_body_b3', styles.detail_text)}>Передача {data.checkIn}</span>
            </div>
          </div>
        </>
      );
    }
  };

  return (
    <div className={styles.similar_property} onClick={handleCardClick}>
      <div className={styles.content_wrapper}>
        {isImageVisible && currentImage && (
          <img
            src={currentImage}
            alt="План объекта"
            className={styles.property_image}
            onError={handleImageError}
            loading="lazy"
          />
        )}
        <div className={styles.property_details}>
          {renderDetails()}
        </div>
      </div>
      <div className={styles.buttons}>
        <div className={styles.buttons}>
          <CardIconButton 
            iconType="icon2" 
            isActive={isFavorite} 
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              handleToggleFavorite();
            }} 
          />
          <CardIconButton 
            iconType="icon3" 
            isActive={isCompared} 
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              handleToggleCompare();
            }} 
          />
        </div>
      </div>
    </div>
  );
}
